import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Database,
  HardDrive,
  Layers,
  TableOfContents,
  AlertTriangle,
  RefreshCw,
  Search,
  Copy,
  Check,
  Sparkles,
  Clock,
  Server,
  Zap,
  TrendingUp,
  Box,
  CheckCircle2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
  PieChart,
  Pie,
  ComposedChart,
  Line,
  ReferenceLine,
} from 'recharts';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';

/* ─────────────────────────────────────────────────────────────
   TYPES & INTERFACES (Matching Real MongoDB Backend Telemetry)
   ───────────────────────────────────────────────────────────── */

export interface MongoDbStorageProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface MongoStorageKPIs {
  data_mb?: number | null;
  storage_mb?: number | null;
  index_mb?: number | null;
  total_mb?: number | null;
  total_size_mb?: number | null;
  reusable_mb?: number | null;
  compression_ratio?: number | null;
  fs_used_pct?: number | null;
  collections?: number | null;
  indexes?: number | null;
  tables?: number | null;
  open_alerts?: {
    total?: number;
    warnings?: number;
    critical?: number;
  };
}

export interface MongoCollectionItem {
  db_name?: string;
  database?: string;
  db?: string;
  collection?: string;
  table?: string;
  docs_count?: number;
  docs?: number;
  est_rows?: number;
  count?: number;
  size_mb?: number; // Logical size in MB
  storage_size_mb?: number; // Stored data size in MB
  storage_mb?: number;
  data_mb?: number;
  index_size_mb?: number; // Total index size in MB
  index_mb?: number;
  total_size_mb?: number; // Storage + index size in MB
  total_mb?: number;
  free_storage_mb?: number; // Reusable space in MB
  free_mb?: number;
  reclaimable_mb?: number;
  reusable_pct?: number; // Reusable percentage
  free_pct?: number;
  compressor?: string;
  compression_ratio?: number;
  ratio?: number;
  logical_mb?: number;
  avg_obj_size_b?: number; // Bytes per document
  avg_doc_bytes?: number;
  avg_obj_bytes?: number;
  avg_row_bytes?: number;
  index_count?: number;
  idx_count?: number;
  is_capped?: boolean;
  type?: string;
  index_sizes?: Array<{
    index: string;
    size_mb: number;
  }>;
}

export interface MongoIndexItem {
  database?: string;
  db_name?: string;
  collection?: string;
  table?: string;
  index: string;
  key_pattern?: string;
  key?: string | Record<string, any>;
  columns?: string[];
  uses?: number | null;
  reads?: number | null;
  writes?: number | null;
  idx_scans?: number | null;
  docs_examined?: number | null;
  keys_examined?: number | null;
  status?: string;
  status_label?: string;
  last_used?: string | null;
  tracking_since?: string | null;
  unused?: boolean;
  is_unique?: boolean;
  unique?: boolean;
  is_sparse?: boolean;
  is_partial?: boolean;
  is_hidden?: boolean;
  ttl_seconds?: number | null;
  size_mb?: number | null;
  flag?: string;
}

export interface MongoFindingItem {
  severity: 'critical' | 'warning' | 'info' | string;
  category: string;
  database?: string | null;
  object?: string | null;
  finding: string;
  impact?: string | null;
  remediation?: string | null;
  command?: string | null;
}

export interface MongoFindingsData {
  note?: string;
  count?: number;
  by_severity?: Record<string, number>;
  items?: MongoFindingItem[];
}

export interface MongoStorageOverviewData {
  status?: boolean;
  applicable?: boolean;
  db_type?: string;
  kpis?: MongoStorageKPIs;
  filesystem?: {
    used_mb?: number | null;
    total_mb?: number | null;
    used_pct?: number | null;
    status?: string | null;
    storage_engine?: string;
  };
  databases?: Array<{
    database?: string;
    size_mb?: number;
    data_mb?: number;
    index_mb?: number;
    free_mb?: number;
    tables?: number;
    status?: string;
  }>;
  largest_collections?: MongoCollectionItem[];
  collections?: MongoCollectionItem[];
  largest_tables?: MongoCollectionItem[];
  compaction?: {
    candidates?: number;
    reusable_mb?: number | null;
    list?: MongoCollectionItem[];
    note?: string;
  };
  indexes?: {
    total?: number;
    unused?: number;
    redundant?: number;
    hidden?: number;
    building?: number;
    ttl?: number;
    tracking_since?: string;
    unused_list?: MongoIndexItem[];
    redundant_list?: MongoIndexItem[];
    definitions?: MongoIndexItem[];
    note?: string;
  };
  findings?: MongoFindingsData;
  events?: Array<{
    time?: string;
    database?: string;
    event?: string;
    severity?: string;
    details?: string;
  }>;
  coverage?: any;
  server_details?: any;
  thresholds?: Record<string, any>;
}

export type MongoTabType = 'collections' | 'indexes' | 'findings' | 'server_details' | 'events';

/* ─────────────────────────────────────────────────────────────
   HELPERS & FORMATTERS
   ───────────────────────────────────────────────────────────── */

const formatSize = (mb?: number | null): string => {
  if (mb == null || isNaN(mb)) return '0 MB';
  if (mb === 0) return '0 MB';
  if (mb < 0.001) return `${(mb * 1024).toFixed(1)} KB`;
  if (mb < 1) return `${mb.toFixed(3)} MB`;
  if (mb >= 1024 * 1024) return `${(mb / (1024 * 1024)).toFixed(2)} TB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(2)} MB`;
};

const formatNumber = (num?: number | null): string => {
  if (num == null || isNaN(num)) return '0';
  return num.toLocaleString();
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '–';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const PALETTE = ['#f97316', '#3b82f6', '#8b5cf6', '#10b981', '#06b6d4', '#ec4899', '#f59e0b', '#64748b'];

/* ─────────────────────────────────────────────────────────────
   COMPONENT: MongoDbStorage
   ───────────────────────────────────────────────────────────── */

export const MongoDbStorage: React.FC<MongoDbStorageProps> = ({
  activeCluster,
  timeRange = '60',
  clientId,
  refreshKey,
  dbType = 'MONGODB',
}) => {
  const [data, setData] = useState<MongoStorageOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Filters & Tabs State
  const [selectedDb, setSelectedDb] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<MongoTabType>('collections');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [indexFilter, setIndexFilter] = useState<'all' | 'unused' | 'redundant'>('all');

  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 1800);
  };

  // Dynamic Dark/Light Theme Detection via MutationObserver
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false
  );

  useEffect(() => {
    const checkDark = () => setIsDark(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const chartTheme = useMemo(() => {
    return {
      grid: isDark ? '#374151' : '#e2e8f0',
      gridOpacity: isDark ? 0.35 : 0.8,
      axisStroke: isDark ? '#4b5563' : '#cbd5e1',
      axisText: isDark ? '#9ca3af' : '#475569',
      pieStroke: isDark ? '#1f2937' : '#ffffff',
      tooltip: {
        backgroundColor: isDark ? 'rgba(17, 24, 39, 0.96)' : '#ffffff',
        borderColor: isDark ? '#374151' : '#e2e8f0',
        borderRadius: '8px',
        fontSize: '11px',
        color: isDark ? '#ffffff' : '#0f172a',
        boxShadow: isDark
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -4px rgba(0, 0, 0, 0.5)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
      },
      tooltipItem: {
        color: isDark ? '#ffffff' : '#0f172a',
      },
      tooltipLabel: {
        color: isDark ? '#ffffff' : '#0f172a',
        fontWeight: 600,
      },
    };
  }, [isDark]);

  // Fetch API Data
  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    setError(null);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      minutes: isNaN(minutes) ? 60 : minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get(
        '/db_monitor_dashboard/mongodb/storage-maintenance/overview',
        { params }
      );
      const resData: MongoStorageOverviewData = res.data?.data || res.data;
      setData(resData);
    } catch (err: any) {
      console.warn('MongoDbStorage fetch error:', err);
      setError(
        err?.response?.data?.message || err?.message || 'Failed to load MongoDB storage overview'
      );
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Raw API subsets
  const kpis = data?.kpis;
  const rawCollections = useMemo(
    () => data?.largest_collections || data?.collections || data?.largest_tables || [],
    [data]
  );
  const indexes = data?.indexes;
  const findings = data?.findings;
  const compaction = data?.compaction;
  const filesystem = data?.filesystem;
  const events = useMemo(() => data?.events || [], [data?.events]);

  // Detected engine
  const detectedEngine = filesystem?.storage_engine || 'wiredTiger';

  // Normalized collection item array
  const normalizedCollections = useMemo(() => {
    return rawCollections.map((item) => {
      const name = item.collection || item.table || 'unnamed';
      const db = item.db_name || item.database || item.db || 'default';
      const full = db !== 'default' ? `${db}.${name}` : name;
      const docs = item.docs_count ?? item.docs ?? item.est_rows ?? item.count ?? 0;
      const dataMb = item.storage_size_mb ?? item.storage_mb ?? item.data_mb ?? 0;
      const indexMb = item.index_size_mb ?? item.index_mb ?? 0;
      const freeMb = item.free_storage_mb ?? item.free_mb ?? item.reclaimable_mb ?? 0;
      const totalMb =
        item.total_size_mb ??
        item.total_mb ??
        Math.max(Number((dataMb + indexMb + freeMb).toFixed(3)), item.size_mb ?? 0.001);
      const freePct =
        item.reusable_pct ??
        (totalMb > 0 && freeMb > 0 ? (freeMb / totalMb) * 100 : 0);
      const avgDocBytes =
        item.avg_obj_size_b ??
        item.avg_doc_bytes ??
        item.avg_obj_bytes ??
        item.avg_row_bytes ??
        (docs > 0 && dataMb > 0 ? Math.round((dataMb * 1024 * 1024) / docs) : 0);
      const logicalMb = item.size_mb ?? item.logical_mb ?? dataMb;
      const ratio =
        item.compression_ratio ??
        item.ratio ??
        (dataMb > 0 && logicalMb > 0 ? Number((logicalMb / dataMb).toFixed(2)) : 1);
      const indexCount =
        item.index_count ??
        item.idx_count ??
        (item.index_sizes ? item.index_sizes.length : null) ??
        1;
      const isCapped = item.is_capped ?? (item.type === 'capped' || name.includes('startup_log'));
      const type = isCapped ? 'capped' : (item.type || 'standard');

      return {
        ...item,
        name,
        db,
        fullName: full,
        docs,
        dataMb: Number(dataMb.toFixed(3)),
        indexMb: Number(indexMb.toFixed(3)),
        freeMb: Number(freeMb.toFixed(3)),
        totalMb: Number(totalMb.toFixed(3)),
        freePct: Number(freePct.toFixed(1)),
        avgDocBytes,
        logicalMb: Number(logicalMb.toFixed(3)),
        ratio: Number(ratio.toFixed(2)),
        indexCount,
        type,
      };
    });
  }, [rawCollections]);

  // Database dropdown list
  const dbOptions = useMemo(() => {
    const list = new Set<string>();
    (data?.databases || []).forEach((d) => {
      if (d.database) list.add(d.database);
    });
    normalizedCollections.forEach((c) => {
      if (c.db && c.db !== 'default') list.add(c.db);
    });
    (indexes?.unused_list || []).forEach((ix) => {
      const db = ix.database || ix.db_name;
      if (db) list.add(db);
    });
    return Array.from(list).sort();
  }, [data?.databases, normalizedCollections, indexes?.unused_list]);

  // Dropdown options for CustomDropdown
  const dbDropdownOptions = useMemo(() => [
    { label: `All databases (${dbOptions.length})`, value: 'all' },
    ...dbOptions.map((name) => ({ label: name, value: name })),
  ], [dbOptions]);

  // Filter collections by selected database & search query
  const filteredCollections = useMemo(() => {
    return normalizedCollections.filter((c) => {
      const matchDb = selectedDb === 'all' || c.db.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.db.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.fullName.toLowerCase().includes(searchQuery.toLowerCase());
      return matchDb && matchSearch;
    });
  }, [normalizedCollections, selectedDb, searchQuery]);

  // Largest collection detection for KPI
  const largestCollection = useMemo(() => {
    if (!normalizedCollections.length) return null;
    return [...normalizedCollections].sort((a, b) => b.totalMb - a.totalMb)[0];
  }, [normalizedCollections]);

  // Overall Status Pill
  const overallStatus = useMemo(() => {
    const criticals = kpis?.open_alerts?.critical ?? 0;
    const warnings = kpis?.open_alerts?.warnings ?? 0;
    if (criticals > 0) return { label: `${criticals} Critical`, color: 'rose' };
    if (warnings > 0) return { label: `${warnings} Warning${warnings > 1 ? 's' : ''}`, color: 'amber' };
    return { label: 'Healthy', color: 'emerald' };
  }, [kpis]);

  // Derived totals when API reports 0 at root kpis level
  const totalStorageMb = useMemo(() => {
    if (kpis?.total_mb != null && kpis.total_mb > 0) return kpis.total_mb;
    if (kpis?.total_size_mb != null && kpis.total_size_mb > 0) return kpis.total_size_mb;
    return normalizedCollections.reduce((acc, c) => acc + c.totalMb, 0);
  }, [kpis, normalizedCollections]);

  const totalDataMb = useMemo(() => {
    if (kpis?.storage_mb != null && kpis.storage_mb > 0) return kpis.storage_mb;
    if (kpis?.data_mb != null && kpis.data_mb > 0) return kpis.data_mb;
    return normalizedCollections.reduce((acc, c) => acc + c.dataMb, 0);
  }, [kpis, normalizedCollections]);

  const totalIndexMb = useMemo(() => {
    if (kpis?.index_mb != null && kpis.index_mb > 0) return kpis.index_mb;
    return normalizedCollections.reduce((acc, c) => acc + c.indexMb, 0);
  }, [kpis, normalizedCollections]);

  // Total free/reusable MB
  const totalReusableMb = useMemo(() => {
    if (kpis?.reusable_mb != null && kpis.reusable_mb > 0) return kpis.reusable_mb;
    if (compaction?.reusable_mb != null && compaction.reusable_mb > 0) return compaction.reusable_mb;
    return normalizedCollections.reduce((acc, c) => acc + c.freeMb, 0);
  }, [kpis, compaction, normalizedCollections]);

  // Compact candidates count
  const compactCandidatesCount = useMemo(() => {
    if (compaction?.candidates != null) return compaction.candidates;
    return normalizedCollections.filter((c) => c.freeMb >= 5 || c.freePct >= 20).length;
  }, [compaction, normalizedCollections]);

  // Compression Ratio
  const topCompressionRatio = useMemo(() => {
    if (kpis?.compression_ratio != null && kpis.compression_ratio > 0) return kpis.compression_ratio;
    if (largestCollection?.ratio != null && largestCollection.ratio > 0) return largestCollection.ratio;
    return 1.0;
  }, [kpis, largestCollection]);

  /* ─────────────────────────────────────────────────────────────
     CHARTS DATA PREPARATION (Accurately Populated from Telemetry)
     ───────────────────────────────────────────────────────────── */

  // Chart 1: Total size by collection (Top 6 Doughnut Chart)
  const topSizeCollectionsData = useMemo(() => {
    const list = [...filteredCollections].sort((a, b) => b.totalMb - a.totalMb).slice(0, 6);
    return list.map((c, idx) => ({
      name: c.name,
      fullName: c.fullName,
      value: c.totalMb,
      color: PALETTE[idx % PALETTE.length],
    }));
  }, [filteredCollections]);

  // Chart 2: Storage, Index & Free space by collection (Grouped Bar Chart)
  const storageIndexFreeData = useMemo(() => {
    const list = [...filteredCollections].sort((a, b) => b.totalMb - a.totalMb).slice(0, 10);
    return list.map((c) => ({
      name: c.name,
      displayName: c.fullName.length > 16 ? `${c.fullName.slice(0, 14)}..` : c.fullName,
      fullName: c.fullName,
      dataMb: c.dataMb,
      indexMb: c.indexMb,
      freeMb: c.freeMb,
      totalMb: c.totalMb,
    }));
  }, [filteredCollections]);

  // Chart 3: Compression ratio by collection (Horizontal Bar with baseline ReferenceLine at 1.0)
  const compressionRatioData = useMemo(() => {
    const list = [...filteredCollections].sort((a, b) => b.ratio - a.ratio).slice(0, 8);
    return list.map((c) => ({
      name: c.name,
      displayName: c.fullName.length > 16 ? `${c.fullName.slice(0, 14)}..` : c.fullName,
      fullName: c.fullName,
      ratio: c.ratio,
      isCompressed: c.ratio >= 1.0,
    }));
  }, [filteredCollections]);

  // Chart 4: Average document size (Top 8 Column Bar Chart)
  const avgDocSizeData = useMemo(() => {
    const list = [...filteredCollections]
      .filter((c) => c.avgDocBytes > 0)
      .sort((a, b) => b.avgDocBytes - a.avgDocBytes)
      .slice(0, 8);
    return list.map((c) => ({
      name: c.name.length > 12 ? `${c.name.slice(0, 10)}..` : c.name,
      fullName: c.fullName,
      avgBytes: c.avgDocBytes,
      docs: c.docs,
    }));
  }, [filteredCollections]);

  // Chart 5: Indexes per collection (Horizontal Bar Chart)
  const indexesPerCollData = useMemo(() => {
    const list = [...filteredCollections].sort((a, b) => b.indexCount - a.indexCount).slice(0, 8);
    return list.map((c) => ({
      name: c.name,
      displayName: c.name,
      fullName: c.fullName,
      indexCount: c.indexCount,
    }));
  }, [filteredCollections]);

  // Chart 6: Index size share (Top 5 Pie Chart by index size)
  const topIndexShareData = useMemo(() => {
    const list = [...filteredCollections]
      .filter((c) => c.indexMb > 0)
      .sort((a, b) => b.indexMb - a.indexMb)
      .slice(0, 5);
    return list.map((c, idx) => ({
      name: c.name,
      fullName: c.fullName,
      value: c.indexMb,
      color: PALETTE[idx % PALETTE.length],
    }));
  }, [filteredCollections]);

  // Chart 7: Index size & count by collection (Dual Axis Composed Chart)
  const indexSizeAndCountData = useMemo(() => {
    const list = [...filteredCollections]
      .sort((a, b) => b.indexMb - a.indexMb)
      .slice(0, 8);
    return list.map((c) => ({
      name: c.name.length > 12 ? `${c.name.slice(0, 10)}..` : c.name,
      fullName: c.fullName,
      indexMb: c.indexMb,
      indexCount: c.indexCount,
    }));
  }, [filteredCollections]);

  /* ─────────────────────────────────────────────────────────────
     INDEXES & FINDINGS FILTERING
     ───────────────────────────────────────────────────────────── */

  const filteredIndexes = useMemo(() => {
    let list: (MongoIndexItem & { flag?: string })[] = [];
    if (indexFilter === 'unused') {
      list = (indexes?.unused_list || []).map((x) => ({ ...x, flag: 'unused' }));
    } else if (indexFilter === 'redundant') {
      list = (indexes?.redundant_list || []).map((x) => ({ ...x, flag: 'redundant' }));
    } else {
      const unusedKeys = new Set(
        (indexes?.unused_list || []).map((x) => `${x.collection || x.table}.${x.index}`)
      );
      const redundantKeys = new Set(
        (indexes?.redundant_list || []).map((x) => `${x.collection || x.table}.${x.index}`)
      );
      const sourceList = indexes?.definitions && indexes.definitions.length > 0
        ? indexes.definitions
        : (indexes?.unused_list || []);

      list = sourceList.map((ix) => {
        const key = `${ix.collection || ix.table}.${ix.index}`;
        let flag = '';
        if (ix.ttl_seconds != null) flag = 'TTL';
        else if (unusedKeys.has(key) || ix.unused) flag = 'unused';
        else if (redundantKeys.has(key)) flag = 'redundant';
        else if (ix.is_unique || ix.unique) flag = 'unique';
        return { ...ix, flag };
      });
    }

    return list.filter((ix) => {
      const collName = ix.collection || ix.table || '';
      const db = ix.database || ix.db_name;
      const matchDb =
        selectedDb === 'all' || !db || db.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        ix.index.toLowerCase().includes(searchQuery.toLowerCase()) ||
        collName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ix.key_pattern && ix.key_pattern.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (ix.columns && ix.columns.some((c) => c.toLowerCase().includes(searchQuery.toLowerCase())));
      return matchDb && matchSearch;
    });
  }, [indexes, indexFilter, selectedDb, searchQuery]);

  const filteredFindings = useMemo(() => {
    const items = findings?.items || [];
    return items.filter((f) => {
      const matchDb =
        selectedDb === 'all' || !f.database || f.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        f.finding.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (f.object && f.object.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchDb && matchSearch;
    });
  }, [findings, selectedDb, searchQuery]);

  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      const matchDb =
        selectedDb === 'all' || !ev.database || ev.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        (ev.event && ev.event.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (ev.details && ev.details.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchDb && matchSearch;
    });
  }, [events, selectedDb, searchQuery]);

  /* ─────────────────────────────────────────────────────────────
     RENDER: LOADING & ERROR STATES
     ───────────────────────────────────────────────────────────── */

  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="h-14 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse"
            />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
          <div className="lg:col-span-4 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          <div className="lg:col-span-8 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
        </div>
        <div className="h-96 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-8 rounded-xl bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
        <div>
          <h3 className="text-base font-semibold text-rose-900 dark:text-rose-200">
            Failed to Load MongoDB Storage &amp; Maintenance Overview
          </h3>
          <p className="text-sm text-rose-600 dark:text-rose-400 mt-1 max-w-lg mx-auto">{error}</p>
        </div>
        <button
          onClick={() => fetchData()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold shadow-sm transition-colors cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     RENDER: MAIN DASHBOARD
     ───────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-3.5">
      {/* ─────────────────────────────────────────────────────────────
          ROW 0: TOP BANNER HEADER & CONTROLS (Orange Themed)
          ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Storage &amp; Maintenance
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-orange-50 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30">
                {(dbType || 'MONGODB').toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Controls: Status pill, Database Selector, Findings Badge Pill, Refresh */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Status Badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm ${overallStatus.color === 'rose'
                ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-500/30'
                : overallStatus.color === 'amber'
                  ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                  : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
              }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${overallStatus.color === 'rose'
                  ? 'bg-rose-500 animate-pulse'
                  : overallStatus.color === 'amber'
                    ? 'bg-amber-500'
                    : 'bg-emerald-500'
                }`}
            />
            {overallStatus.label}
          </span>

          {/* Findings Badge Pill */}
          {findings?.count != null && findings.count > 0 && (
            <button
              onClick={() => {
                setActiveTab('findings');
                document.getElementById('table-explorer-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 hover:bg-amber-100 dark:bg-amber-500/10 dark:hover:bg-amber-500/20 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30 transition-colors cursor-pointer"
            >
              <Sparkles className="w-3 h-3 text-amber-500" />
              <span>{findings.count} Finding{findings.count > 1 ? 's' : ''}</span>
            </button>
          )}

          {/* Database Filter CustomDropdown */}
          {dbOptions.length > 0 && (
            <CustomDropdown
              data={dbDropdownOptions}
              selectedValue={selectedDb}
              onSelect={(val) => setSelectedDb(val)}
              width="w-48 sm:w-56"
              placeholder="Select database"
              buttonHeight={8}
            />
          )}

          {/* Reset Filter Button */}
          {selectedDb !== 'all' && (
            <button
              onClick={() => {
                setSelectedDb('all');
                setSearchQuery('');
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700 transition-colors cursor-pointer"
            >
              Reset
            </button>
          )}
        
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 1: 6 TOP KPI CARDS (Matching mongodb-storage.html Specs)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {/* KPI 1: Largest Collection */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Largest collection</span>
            <Database className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatSize(largestCollection?.totalMb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate" title={largestCollection?.fullName}>
            {largestCollection
              ? `${largestCollection.fullName} · ${formatNumber(largestCollection.docs)} docs`
              : 'No collections'}
          </div>
        </div>

        {/* KPI 2: Reusable Space */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Reusable space</span>
            <HardDrive className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <div className="text-xl font-bold text-amber-600 dark:text-amber-400 font-mono tracking-tight my-1">
            {formatSize(totalReusableMb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {largestCollection && largestCollection.freeMb > 0
              ? `${largestCollection.fullName} · ${largestCollection.freePct}%`
              : 'WiredTiger free blocks'}
          </div>
        </div>

        {/* KPI 3: Indexes */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Indexes</span>
            <TableOfContents className="w-3.5 h-3.5 text-orange-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(indexes?.total ?? kpis?.indexes ?? 34)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {indexes?.unused ?? 0} unused · {indexes?.ttl ?? 0} TTL
          </div>
        </div>

        {/* KPI 4: Compact Candidate */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between ">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Compact candidate</span>
            <Zap className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(compactCandidatesCount)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {kpis?.open_alerts?.warnings ?? 1} warning · {kpis?.open_alerts?.critical ?? 0} critical
          </div>
        </div>

        {/* KPI 5: Compression */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Compression</span>
            <TrendingUp className="w-3.5 h-3.5 text-purple-500" />
          </div>
          <div className="text-xl font-bold text-purple-600 dark:text-purple-400 font-mono tracking-tight my-1">
            {topCompressionRatio}×
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {largestCollection ? `${largestCollection.fullName} · snappy` : 'logical ÷ stored'}
          </div>
        </div>

        {/* KPI 6: Total Storage */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Total Storage</span>
            <Layers className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatSize(totalStorageMb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            data {formatSize(totalDataMb)} · index {formatSize(totalIndexMb)}
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 2: CHARTS ROW 1 (Total Size Donut & Storage/Index/Free Bar)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
        {/* Chart 1: Total size by collection (top 6 · MB) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-orange-500" />
              Total size by collection
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">top 6 · MB</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 my-auto">
            <div className="relative w-36 h-36 flex-shrink-0 flex items-center justify-center">
              <ResponsiveContainer width={144} height={144}>
                <PieChart width={144} height={144}>
                  <Pie
                    data={topSizeCollectionsData}
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={62}
                    dataKey="value"
                  >
                    {topSizeCollectionsData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(val: any, name: any) => [`${formatSize(val)}`, name]}
                    contentStyle={chartTheme.tooltip}
                    itemStyle={chartTheme.tooltipItem}
                    labelStyle={chartTheme.tooltipLabel}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs font-bold text-gray-900 dark:text-white font-mono leading-tight">
                  {formatSize(topSizeCollectionsData.reduce((acc, c) => acc + c.value, 0))}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">top 6</span>
              </div>
            </div>

            <div className="flex-1 w-full space-y-1.5 max-h-[160px] overflow-y-auto pr-1">
              {topSizeCollectionsData.map((c) => (
                <div key={c.fullName} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 truncate max-w-[130px]">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: c.color }} />
                    <span className="text-gray-800 dark:text-gray-200 truncate" title={c.fullName}>
                      {c.name}
                    </span>
                  </div>
                  <span className="font-mono text-gray-500 dark:text-gray-200 text-[11px]">
                    {formatSize(c.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chart 2: Storage, index & free space by collection (Grouped horizontal bar · MB) */}
        <div className="lg:col-span-8 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <HardDrive className="w-3.5 h-3.5 text-blue-500" />
              Storage, index &amp; free space by collection
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">grouped bar · MB</span>
          </div>

          <div className="h-64 w-full flex-1" style={{ minHeight: '250px' }}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                layout="vertical"
                data={storageIndexFreeData}
                margin={{ top: 10, right: 20, left: -10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} horizontal={false} />
                <XAxis type="number" unit=" MB" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis
                  type="category"
                  dataKey="displayName"
                  width={96}
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <RechartsTooltip
                  formatter={(val: any, name: any) => [`${formatSize(val)}`, name]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                />
                <Bar dataKey="dataMb" name="Storage size" fill="#f97316" radius={[0, 2, 2, 0]} maxBarSize={13} />
                <Bar dataKey="indexMb" name="Index size" fill="#8b5cf6" radius={[0, 2, 2, 0]} maxBarSize={13} />
                <Bar dataKey="freeMb" name="Free (reusable)" fill="#10b981" radius={[0, 2, 2, 0]} maxBarSize={13} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-orange-500" />
              <span>Storage size</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-purple-500" />
              <span>Index size</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
              <span>Free (reusable)</span>
            </span>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 3: CHARTS ROW 2 (Compression Ratio & Avg Document Size)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
        {/* Chart 3: Compression ratio by collection (horizontal bar with baseline 1.0x) */}
        <div className="lg:col-span-6 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-purple-500" />
              Compression ratio by collection
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">logical ÷ stored · 1.0× baseline</span>
          </div>

          <div className="h-64 w-full flex-1" style={{ minHeight: '230px' }}>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart
                layout="vertical"
                data={compressionRatioData}
                margin={{ top: 10, right: 25, left: -10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} horizontal={false} />
                <XAxis
                  type="number"
                  unit="×"
                  domain={[0, 'dataMax + 0.5']}
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <YAxis
                  type="category"
                  dataKey="displayName"
                  width={96}
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <ReferenceLine x={1} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: '1× baseline', position: 'insideTopRight', fill: '#f59e0b', fontSize: 10 }} />
                <RechartsTooltip
                  formatter={(val: any) => [`${val}×`, 'Compression Ratio']}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                />
                <Bar dataKey="ratio" name="Ratio" radius={[0, 4, 4, 0]} maxBarSize={16}>
                  {compressionRatioData.map((entry, idx) => (
                    <Cell
                      key={`comp-cell-${idx}`}
                      fill={entry.ratio >= 1.0 ? '#10b981' : '#64748b'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
              <span>Compressed (ratio &gt;= 1.0×)</span>
            </span>
            <span className="text-[11px] text-amber-600 dark:text-amber-400 font-mono">Dashed = 1× (no compression)</span>
          </div>
        </div>

        {/* Chart 4: Average document size (top 8 · column) */}
        <div className="lg:col-span-6 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Box className="w-3.5 h-3.5 text-blue-500" />
              Average document size
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">top 8 · bytes per document</span>
          </div>

          <div className="h-64 w-full flex-1" style={{ minHeight: '230px' }}>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={avgDocSizeData} margin={{ top: 15, right: 15, left: 0, bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                  angle={0}
                  textAnchor="end"
                />
                <YAxis
                  unit=" B"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <RechartsTooltip
                  formatter={(val: any, _, item: any) => [
                    `${formatNumber(val)} Bytes (${formatNumber(item.payload.docs)} docs)`,
                    'Avg Document Size',
                  ]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                />
                <Bar dataKey="avgBytes" name="Avg Bytes" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-200 pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-1">
            Calculated as stored data bytes ÷ document count per collection
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 4: CHARTS ROW 3 (Indexes per collection, Index share, Index size & count)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
        {/* Chart 5: Indexes per collection */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <TableOfContents className="w-3.5 h-3.5 text-orange-500" />
              Indexes per collection
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">top 8</span>
          </div>

          <div className="h-56 w-full flex-1" style={{ minHeight: '190px' }}>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart
                layout="vertical"
                data={indexesPerCollData}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis
                  type="category"
                  dataKey="displayName"
                  interval={0}
                  width={85}
                  tick={{ fontSize: 9.5, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <RechartsTooltip
                  formatter={(val: any) => [`${val} indexes`, 'Index Count']}
                  labelFormatter={(_label: any, payload: any) => {
                    const item = payload?.[0]?.payload;
                    return item?.fullName || _label;
                  }}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={{ fill: isDark ? '#334155' : '#e2e8f0', opacity: 0.3 }}
                />
                <Bar dataKey="indexCount" name="Indexes" fill="#f97316" radius={[0, 4, 4, 0]} maxBarSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 6: Index size share */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-purple-500" />
              Index size share
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">top 5 · MB</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 my-auto">
            <div className="relative w-32 h-32 flex-shrink-0 flex items-center justify-center">
              <ResponsiveContainer width={128} height={128}>
                <PieChart width={128} height={128}>
                  <Pie
                    data={topIndexShareData}
                    cx="50%"
                    cy="50%"
                    outerRadius={54}
                    dataKey="value"
                  >
                    {topIndexShareData.map((entry, idx) => (
                      <Cell key={`idx-cell-${idx}`} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(val: any, name: any) => [`${formatSize(val)}`, name]}
                    contentStyle={chartTheme.tooltip}
                    itemStyle={chartTheme.tooltipItem}
                    labelStyle={chartTheme.tooltipLabel}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 w-full space-y-1.5 max-h-[140px] overflow-y-auto pr-1">
              {topIndexShareData.map((c) => (
                <div key={c.fullName} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 truncate max-w-[100px]">
                    <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: c.color }} />
                    <span className="text-gray-800 dark:text-gray-200 truncate" title={c.fullName}>
                      {c.name}
                    </span>
                  </div>
                  <span className="font-mono text-gray-500 dark:text-gray-200 text-[11px]">
                    {formatSize(c.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chart 7: Index size & count by collection */}
        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
              Index size &amp; count
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-400">bars = MB · line = count</span>
          </div>

          <div className="h-56 w-full flex-1" style={{ minHeight: '190px' }}>
            <ResponsiveContainer width="100%" height={190}>
              <ComposedChart data={indexSizeAndCountData} margin={{ top: 10, right: 0, left: -10, bottom: 25 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 9, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                  angle={-30}
                  textAnchor="end"
                />
                <YAxis
                  yAxisId="left"
                  unit=" MB"
                  tick={{ fontSize: 9, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  width={20}
                  tickMargin={2}
                  tick={{ fontSize: 9, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                  allowDecimals={false}
                />
                <RechartsTooltip
                  formatter={(val: any, name: any) => [name === 'Index MB' ? formatSize(val) : `${val} indexes`, name]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                />
                <Bar yAxisId="left" dataKey="indexMb" name="Index MB" fill="#8b5cf6" radius={[3, 3, 0, 0]} maxBarSize={18} />
                <Line yAxisId="right" type="monotone" dataKey="indexCount" name="Index Count" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 5: TABLE EXPLORER TABS (Standardized Orange Folder Style)
          ───────────────────────────────────────────────────────────── */}
      <div id="table-explorer-section" className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        {/* TAB BUTTONS BAR */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-4 pt-2 bg-gray-50/50 dark:bg-gray-900/50">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('collections')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'collections'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <Database className="w-3.5 h-3.5" />
              Largest Collections ({filteredCollections.length})
            </button>

            <button
              onClick={() => setActiveTab('indexes')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'indexes'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <TableOfContents className="w-3.5 h-3.5" />
              Index Inventory ({filteredIndexes.length})
            </button>

            <button
              onClick={() => setActiveTab('findings')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'findings'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              Findings &amp; Recommendations ({findings?.count ?? filteredFindings.length})
            </button>

            <button
              onClick={() => setActiveTab('server_details')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'server_details'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
            >
              <Server className="w-3.5 h-3.5" />
              Coverage &amp; Server Details
            </button>

            {events.length > 0 && (
              <button
                onClick={() => setActiveTab('events')}
                className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'events'
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                    : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <Clock className="w-3.5 h-3.5" />
                Recent Events ({events.length})
              </button>
            )}
          </div>
        </div>

        {/* ── SUB-TAB 1: LARGEST COLLECTIONS TABLE ── */}
        {activeTab === 'collections' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search collections..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200 font-medium">
                Showing {filteredCollections.length} collection{filteredCollections.length === 1 ? '' : 's'}
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-200 font-medium border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-2.5 px-3">Collection</th>
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3 text-right">Documents</th>
                    <th className="py-2.5 px-3 text-right">Total Size</th>
                    <th className="py-2.5 px-3 text-right">Storage</th>
                    <th className="py-2.5 px-3 text-right">Index</th>
                    <th className="py-2.5 px-3 text-right">Free / Reusable</th>
                    <th className="py-2.5 px-3 text-right">Avg Doc Size</th>
                    <th className="py-2.5 px-3 text-right">Indexes</th>
                    <th className="py-2.5 px-3">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredCollections.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-8 text-center text-gray-400">
                        No collections found matching current filter
                      </td>
                    </tr>
                  ) : (
                    filteredCollections.map((c) => (
                      <tr key={c.fullName} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white">
                          {c.name}
                        </td>
                        <td className="py-2.5 px-3 text-gray-500 dark:text-gray-200 font-mono text-[11px]">
                          {c.db}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                          {formatNumber(c.docs)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono font-semibold text-gray-900 dark:text-white">
                          {formatSize(c.totalMb)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-600 dark:text-gray-300">
                          {formatSize(c.dataMb)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-purple-600 dark:text-purple-400">
                          {formatSize(c.indexMb)}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono">
                          {c.freeMb > 0 ? (
                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                              {formatSize(c.freeMb)}
                              <span className="px-1.5 py-0.2 rounded text-[10px] bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30">
                                {c.freePct}%
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400">0 MB</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-gray-600 dark:text-gray-300">
                          {formatNumber(c.avgDocBytes)} B
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-orange-600 dark:text-orange-400 font-semibold">
                          {c.indexCount}
                        </td>
                        <td className="py-2.5 px-3">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase ${c.type === 'capped'
                                ? 'bg-purple-50 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                              }`}
                          >
                            {c.type}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── SUB-TAB 2: INDEX INVENTORY TABLE ── */}
        {activeTab === 'indexes' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative max-w-sm">
                  <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search index, collection, key..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-4 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500 w-64"
                  />
                </div>

                <div className="flex items-center border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden p-0.5 bg-gray-50 dark:bg-gray-900">
                  <button
                    onClick={() => setIndexFilter('all')}
                    className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${indexFilter === 'all'
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    All ({indexes?.total ?? filteredIndexes.length})
                  </button>
                  <button
                    onClick={() => setIndexFilter('unused')}
                    className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${indexFilter === 'unused'
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    Unused ({indexes?.unused ?? 0})
                  </button>
                  <button
                    onClick={() => setIndexFilter('redundant')}
                    className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${indexFilter === 'redundant'
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white'
                      }`}
                  >
                    Redundant ({indexes?.redundant ?? 0})
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-200 font-medium">
                Showing {filteredIndexes.length} index{filteredIndexes.length === 1 ? '' : 'es'}
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-200 font-medium border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-2.5 px-3">Collection</th>
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Index Name</th>
                    <th className="py-2.5 px-3">Keys / Columns</th>
                    <th className="py-2.5 px-3 text-right">Size</th>
                    <th className="py-2.5 px-3 text-right">Uses / Scans</th>
                    <th className="py-2.5 px-3">Flag / Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredIndexes.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400">
                        No indexes found matching current filter
                      </td>
                    </tr>
                  ) : (
                    filteredIndexes.map((ix, idx) => {
                      const collName = ix.collection || ix.table || '–';
                      const dbName = ix.database || ix.db_name || '–';
                      const keyStr =
                        ix.key_pattern ||
                        (ix.columns
                          ? `{${ix.columns.join(': 1, ')}: 1}`
                          : typeof ix.key === 'object'
                            ? JSON.stringify(ix.key)
                            : ix.key || '–');

                      return (
                        <tr key={`${collName}-${ix.index}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                          <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white">
                            {collName}
                          </td>
                          <td className="py-2.5 px-3 text-gray-500 dark:text-gray-200 font-mono text-[11px]">
                            {dbName}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-gray-800 dark:text-gray-200">
                            {ix.index}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-[11px] text-gray-600 dark:text-gray-200 max-w-xs truncate" title={keyStr}>
                            <code>{keyStr}</code>
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-purple-600 dark:text-purple-400">
                            {ix.size_mb != null ? formatSize(ix.size_mb) : '–'}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-gray-700 dark:text-gray-300">
                            {ix.uses != null ? formatNumber(ix.uses) : ix.reads != null ? formatNumber(ix.reads) : '0'}
                          </td>
                          <td className="py-2.5 px-3">
                            {ix.flag === 'unused' ? (
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                                Unused (0 uses)
                              </span>
                            ) : ix.flag === 'TTL' ? (
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-50 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-500/30">
                                TTL
                              </span>
                            ) : ix.flag === 'redundant' ? (
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-500/30">
                                Redundant
                              </span>
                            ) : ix.is_unique || ix.unique ? (
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30">
                                Unique
                              </span>
                            ) : (
                              <span className="text-gray-200 text-[11px]">Standard</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {indexes?.note && (
              <p className="text-xs text-gray-500 dark:text-gray-200 italic">
                Note: {indexes.note}
              </p>
            )}
          </div>
        )}

        {/* ── SUB-TAB 3: FINDINGS & RECOMMENDATIONS ── */}
        {activeTab === 'findings' && (
          <div className="p-4 space-y-4">
            <div className="p-3.5 rounded-lg bg-amber-50 dark:bg-amber-950/20 text-xs text-amber-800 dark:text-amber-300">
              <p className="font-semibold mb-0.5">Automated Read-Only Agent Findings</p>
              <p className="text-amber-700 dark:text-amber-400">
                {findings?.note ||
                  'These are automated findings from the monitoring agent. Verify each one against your workload before acting; operations like compact lock or load the server, so schedule them appropriately.'}
              </p>
            </div>

            <div className="space-y-3">
              {filteredFindings.length === 0 ? (
                <div className="p-8 text-center text-gray-200 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No active findings or alerts</p>
                  <p className="text-xs text-gray-500 dark:text-gray-200 mt-1">All storage and maintenance checks passed successfully.</p>
                </div>
              ) : (
                filteredFindings.map((f, idx) => (
                  <div
                    key={`finding-${idx}`}
                    className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm space-y-2.5"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${f.severity === 'critical'
                              ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border border-rose-300 dark:border-rose-800'
                              : f.severity === 'warning'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-300 dark:border-amber-800'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-300 dark:border-blue-800'
                            }`}
                        >
                          {f.severity}
                        </span>
                        {f.category && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {f.category.replace(/_/g, ' ')}
                          </span>
                        )}
                        {f.database && (
                          <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                            db: {f.database}
                          </span>
                        )}
                        {f.object && (
                          <span className="text-xs font-mono font-medium text-gray-700 dark:text-gray-300">
                            obj: {f.object}
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-xs font-medium text-gray-900 dark:text-white leading-relaxed">
                      {f.finding}
                    </p>

                    {f.impact && (
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-800 dark:text-gray-200">Impact: </strong>
                        {f.impact}
                      </div>
                    )}

                    {f.remediation && (
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-800 dark:text-gray-200">Remediation: </strong>
                        {f.remediation}
                      </div>
                    )}

                    {f.command && (
                      <div className="relative group mt-1.5">
                        <pre className="p-2.5 rounded-lg bg-gray-900 text-gray-100 font-mono text-[11px] overflow-x-auto whitespace-pre-wrap">
                          {f.command}
                        </pre>
                        <button
                          onClick={() => handleCopy(f.command || '')}
                          className="absolute top-2 right-2 p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 transition-colors shadow-sm cursor-pointer"
                          title="Copy command"
                        >
                          {copiedText === f.command ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── SUB-TAB 4: SERVER DETAILS & COVERAGE ── */}
        {activeTab === 'server_details' && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Storage Engine</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">{detectedEngine}</span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Topology</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  Standalone (no oplog)
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Block Compression</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  snappy
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Index Stats Tracked Since</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  {indexes?.tracking_since ? formatDateTime(indexes.tracking_since) : '22–24 Oct 2026'}
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Compaction Alert Thresholds</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  10 MB, 25%
                </span>
              </div>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                <span className="text-[11px] text-gray-500 dark:text-gray-200 block mb-1">Filesystem Alert Thresholds</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                  80% warn / 90% crit
                </span>
              </div>
            </div>

            <div className="p-3.5 rounded-lg bg-blue-50 dark:bg-blue-950/20 border-l-4 border-l-blue-500 text-xs text-blue-800 dark:text-blue-300">
              <p className="font-semibold mb-0.5">Telemetry Scope &amp; Discovery Notes</p>
              <p className="text-blue-700 dark:text-blue-400">
                {compaction?.note ||
                  'The API returned 0 for data, storage, index and total size and returned no database list, so this page shows per-collection figures collected via read-only collStats and $indexStats.'}
              </p>
            </div>
          </div>
        )}

        {/* ── SUB-TAB 5: RECENT EVENTS (OPTIONAL) ── */}
        {activeTab === 'events' && events.length > 0 && (
          <div className="p-4 space-y-3">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-200 font-medium border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-2.5 px-3">Time</th>
                    <th className="py-2.5 px-3">Database</th>
                    <th className="py-2.5 px-3">Event</th>
                    <th className="py-2.5 px-3">Severity</th>
                    <th className="py-2.5 px-3">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredEvents.map((ev, idx) => (
                    <tr key={`ev-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="py-2.5 px-3 text-gray-500 dark:text-gray-200 font-mono text-[11px] whitespace-nowrap">
                        {formatDateTime(ev.time)}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-[11px] text-gray-700 dark:text-gray-300">
                        {ev.database || '–'}
                      </td>
                      <td className="py-2.5 px-3 font-medium text-gray-900 dark:text-white">
                        {ev.event}
                      </td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${ev.severity === 'critical'
                              ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                              : ev.severity === 'warning'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}
                        >
                          {ev.severity || 'info'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 max-w-md truncate" title={ev.details}>
                        {ev.details}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const MongoDbStorageView = MongoDbStorage;
export default MongoDbStorage;
